import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { WarningOutlined } from '@ant-design/icons';
import ModalWrapper from 'src/components/common/modal/ModalWrapper';

import { useKSK2022_R01Store } from '../Store_KSK2022_R01';
import { AgGridReact } from 'ag-grid-react';
import { KSK2022_R01InspectDto } from '../Dto_KSK2022_R01';
import { useMouseContent } from 'src/utils/mouse/mouseContent';
import { InputForm } from 'src/components/common/form/input/InputForm';
import { InspectTable_KSK2022_R01 as InspectTableKSK2020R01 } from "../table/InspectTable_KSK2022_R01";
import { useDescText } from 'src/hooks/useDescText';
import { DESC_BTN, DESC_FLD } from 'src/utils/descLanguage/descLanguageUtil';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { buildKSK2022_R01InspectOptionRows } from '../util/inspectBadQtyUtil_KSK2022_R01';
import { showWarning } from 'src/utils/toast/toastUtil';

const MODAL_SIZE_STORAGE_KEY = 'KSK2022_R01-inspect-modal-size';

interface InspectModalSnapshot {
	badQty: number;
	inspectList: KSK2022_R01InspectDto[];
	originalInspectList: KSK2022_R01InspectDto[];
	deletedInspectList: string[];
	selectedInspectList: string[];
}

interface Props {
    inspectGridRef: React.RefObject<AgGridReact<KSK2022_R01InspectDto> | null>;
	defaultKSK2022_R01InspectRow: KSK2022_R01InspectDto;
	handleInspectConfirmClick: () => Promise<void>;
}

export const Modal_KSK2022_R01 = ({
    inspectGridRef,
    defaultKSK2022_R01InspectRow,
	handleInspectConfirmClick,
}: Props) => {
	const { renderDescText } = useDescText();
	const {
		modalVisible, setModalVisible,
		editKSK2022_R01, setEditKSK2022_R01,
		KSK2022_R01InspectList, setKSK2022_R01InspectList,
		setOriginalKSK2022_R01InspectList,
		setDeletedKSK2022_R01InspectList,
		setSelectedKSK2022_R01InspectList,
	 } = useKSK2022_R01Store();
	const { faultyValueList } = useCommonCodeStore();
	const actionLabels = useMemo(() => [
		renderDescText(DESC_BTN.CONFIRM, "확인"),
		renderDescText(DESC_BTN.CANCEL, "취소"),
	], [renderDescText]);
	const badQtyTitle = renderDescText(DESC_FLD.DEFECT_QTY, "불량수량");
	const modalSnapshotRef = useRef<InspectModalSnapshot | null>(null);
	const handleBadQtyChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		if (!editKSK2022_R01) return;
		const inputBadQty = Math.max(0, Number(e.target.value) || 0);
		const maxInspectBadQty = KSK2022_R01InspectList.reduce(
			(maxQty, item) => Math.max(maxQty, Math.max(0, Number(item.badQty) || 0)),
			0,
		);
		const validBadQty = Math.max(inputBadQty, maxInspectBadQty);
		if (inputBadQty < maxInspectBadQty) {
			showWarning(`불량수량은 불량내역 사유별 최대 수량(${maxInspectBadQty})보다 작을 수 없습니다.`);
		}

		setEditKSK2022_R01({
			...editKSK2022_R01,
			badQty: validBadQty,
		});
	}, [editKSK2022_R01, KSK2022_R01InspectList, setEditKSK2022_R01]);

    const modalRef = useRef<HTMLDivElement>(null);
    const modalInfoRef = useRef<HTMLDivElement>(null);
    useRef(false);
    useEffect(() => {
		if (!modalVisible) {
			modalSnapshotRef.current = null;
			return;
		}
		if (modalSnapshotRef.current) return;

		// 모달을 열 때 불량사유 공통코드 전체를 행으로 구성하고 기존 내역을 해당 행에 반영한다.
		const currentState = useKSK2022_R01Store.getState();
		const currentInspectList = currentState.KSK2022_R01InspectList;
		modalSnapshotRef.current = {
			badQty: Number(currentState.editKSK2022_R01?.badQty) || 0,
			inspectList: currentInspectList.map(row => ({ ...row })),
			originalInspectList: currentState.originalKSK2022_R01InspectList.map(row => ({ ...row })),
			deletedInspectList: [...currentState.deletedKSK2022_R01InspectList],
			selectedInspectList: [...currentState.selectedKSK2022_R01InspectList],
		};
		const optionRows = buildKSK2022_R01InspectOptionRows(
			faultyValueList,
			currentInspectList,
			// FAULTY_VALUE.ref02는 불량사유 이름을 조회할 다국어 코드로 사용한다.
			faultyValue => renderDescText(faultyValue.ref02, faultyValue.codeNm),
		);
		setKSK2022_R01InspectList(optionRows);
		setOriginalKSK2022_R01InspectList(optionRows);
		setDeletedKSK2022_R01InspectList([]);
		setSelectedKSK2022_R01InspectList([]);
	}, [
		faultyValueList,
		modalVisible,
		renderDescText,
		setDeletedKSK2022_R01InspectList,
		setKSK2022_R01InspectList,
		setOriginalKSK2022_R01InspectList,
		setSelectedKSK2022_R01InspectList,
	]);

	const handleCancelClick = useCallback(() => {
		const snapshot = modalSnapshotRef.current;
		if (snapshot) {
			const currentEdit = useKSK2022_R01Store.getState().editKSK2022_R01;
			if (currentEdit) {
				setEditKSK2022_R01({
					...currentEdit,
					badQty: snapshot.badQty,
				});
			}
			setKSK2022_R01InspectList(snapshot.inspectList.map(row => ({ ...row })));
			setOriginalKSK2022_R01InspectList(snapshot.originalInspectList.map(row => ({ ...row })));
			setDeletedKSK2022_R01InspectList([...snapshot.deletedInspectList]);
			setSelectedKSK2022_R01InspectList([...snapshot.selectedInspectList]);
		}

		modalSnapshotRef.current = null;
		setModalVisible(false);
	}, [
		setDeletedKSK2022_R01InspectList,
		setEditKSK2022_R01,
		setKSK2022_R01InspectList,
		setModalVisible,
		setOriginalKSK2022_R01InspectList,
		setSelectedKSK2022_R01InspectList,
	]);

    if (!modalVisible) return null;

    return (
        // KSK2022_R01 불량 등록 화면에만 고정 폭과 높이를 적용한다.
        <ModalWrapper
			defaultWidth={700}
			defaultHeight={700}
			sizeStorageKey={MODAL_SIZE_STORAGE_KEY}
            overlay={true}
            onOverlayClick={handleCancelClick}
            onEscKeyDown={handleCancelClick}
        >
			<div className="flex h-full min-h-[460px] flex-col overflow-hidden rounded-[6px] bg-[#f5f8fc]">
				<div className="flex h-[58px] shrink-0 items-center gap-[10px] bg-gradient-to-r from-[#123f78] to-[#245d98] px-[22px] text-white">
					<WarningOutlined className="text-[20px]" />
					<p className="text-[21px] font-bold">
						{renderDescText(DESC_FLD.DEFECT_DETAILS_REGISTRATION, "불량 내용 등록")}
					</p>
				</div>

				<div className="flex min-h-0 flex-1 flex-col gap-[10px] p-[16px]">
					<div className="flex shrink-0 items-center justify-end rounded-[10px] border border-[#d8e3f0] bg-white p-[10px] shadow-sm">
						<div className="min-w-[270px] w-full max-w-[360px]">
							<InputForm
								title={badQtyTitle}
								value={editKSK2022_R01?.badQty ?? 0}
								editable={true}
								name="badQty"
								onChange={handleBadQtyChange}
								dragSelectAll={true}
								wrapperOption={{
									wrapperStyle: "!h-[40px] w-full min-w-0",
									titleStyle: "flex h-[40px] w-[120px] min-w-[120px] items-center rounded-[8px] bg-[#e8eff8] px-[10px] text-left text-[15px] font-bold whitespace-nowrap text-[#183153]",
									inputStyle: "flex-1 min-w-0 [&>input]:!h-[40px] [&>input]:!rounded-[8px] [&>input]:!border-[#c5d5e9] [&>input]:!px-[10px] [&>input]:text-[16px]",
								}}
							/>
						</div>
					</div>

					<div className="min-h-0 flex-1 overflow-hidden rounded-[10px] border border-[#d8e3f0] bg-white shadow-sm" ref={modalRef}>
						<div className="h-full w-full" ref={modalInfoRef}>
							<InspectTableKSK2020R01 gridRef={inspectGridRef} defaultKSK2022_R01InspectRow={defaultKSK2022_R01InspectRow} />
						</div>
					</div>
				</div>

				<div className="flex shrink-0 items-center justify-end gap-[8px] border-t border-[#d8e3f0] bg-white px-[18px] py-[12px]">
					<button
						type="button"
						className="h-[40px] min-w-[86px] rounded-[8px] border border-[#bdcadd] bg-white px-[16px] text-[15px] font-bold text-[#52657d] shadow-sm hover:bg-[#eef2f7]"
						onClick={handleCancelClick}
					>
						{actionLabels[1]}
					</button>
					<button
						type="button"
						className="h-[40px] min-w-[86px] rounded-[8px] bg-gradient-to-b from-[#2f67ec] to-[#1646b8] px-[16px] text-[15px] font-bold text-white shadow-md hover:brightness-105"
						onClick={() => void handleInspectConfirmClick()}
					>
						{actionLabels[0]}
					</button>
				</div>
			</div>
        </ModalWrapper>
    );
};
